import React, { useEffect, useState, useRef, useMemo, createRef } from 'react';
import { useLocation } from 'react-router-dom';
import PropTypes from 'prop-types';
import { Box, Drawer } from '@mui/material';
import makeStyles from '@mui/styles/makeStyles';
import SwipeableViews from 'react-swipeable-views';
import SimpleBar from 'simplebar-react';
import clsx from 'clsx';

import UserProfile from 'src/components/widgets/UserProfile';
import NavBarDivider from 'src/components/widgets/NavBarDivider';
import MainPageMenu from './MainPageMenu';
import MyCollectionPageMenu from './MyCollectionPageMenu';
import NavBarHeader from './NavBarHeader';

const drawerWidth = 256;
const useStyles = makeStyles((theme) => ({
  drawer: {
    overflowX: 'hidden',
    flexShrink: 0,
    whiteSpace: 'nowrap',
    backgroundColor: theme.palette.background.sidebar,
    height: ({ bannerHeight }) => `calc(100% - ${bannerHeight}px)`,
    top: ({ bannerHeight }) => bannerHeight,
    borderRadius: '0'
  },
  drawerLightMode: {
    borderRight: '0px'
  },
  drawerOpen: {
    width: drawerWidth,
    transition: theme.transitions.create('width', {
      easing: theme.transitions.easing.sharp,
      duration: theme.transitions.duration.enteringScreen
    })
  },
  drawerClose: {
    width: theme.spacing(12),
    transition: theme.transitions.create('width', {
      easing: theme.transitions.easing.sharp,
      duration: theme.transitions.duration.leavingScreen
    })
  },
  swipeableViewsWrapper: {
    marginRight: theme.spacing(1),
    paddingRight: theme.spacing(1),
    paddingLeft: theme.spacing(1),
    overflowX: 'hidden'
  },
  footer: {
    width: 248
    // backgroundColor: theme.palette.custom.navbar
  }
}));

const NavBar = ({ bannerHeight }) => {
  const classes = useStyles({ bannerHeight });
  const location = useLocation();
  const [drawerMode, setDrawerMode] = useState('full');
  const scrollableNodeRef = createRef();
  const [navBarHeaderShadow, setNavBarHeaderShadow] = useState(false);
  const ref = useRef(null);
  const handleToggleDrawer = () => {
    setDrawerMode(drawerMode === 'full' ? 'mini' : 'full');
  };
  const activeNav = useMemo(() => {
    if (/^\/app\/manage\/my-collections/.test(location.pathname)) {
      return 1;
    }
    return 0;
  }, [location.pathname]);

  useEffect(() => {
    ref?.current();
  }, [drawerMode]);

  useEffect(() => {
    const handler = (e) => {
      if (e?.target.scrollTop > 10) {
        setNavBarHeaderShadow(true);
      } else {
        setNavBarHeaderShadow(false);
      }
    };
    scrollableNodeRef?.current?.addEventListener('scroll', handler);
    return () => {
      scrollableNodeRef?.current?.removeEventListener('scroll', handler);
    };
  }, [scrollableNodeRef]);

  return (
    <Drawer
      anchor="left"
      variant="permanent"
      PaperProps={{
        elevation: 0
      }}
      className={clsx(classes.drawer, {
        [classes.drawerOpen]: drawerMode === 'full',
        [classes.drawerClose]: drawerMode === 'mini'
      })}
      classes={{
        paper: clsx(classes.drawer, {
          [classes.drawerOpen]: drawerMode === 'full',
          [classes.drawerClose]: drawerMode === 'mini'
        })
      }}
    >
      <NavBarHeader
        drawerMode={drawerMode}
        showBoxShadow={navBarHeaderShadow}
        toggleDrawer={handleToggleDrawer}
      />
      <SimpleBar
        style={{
          maxHeight: `calc(100% -  ${drawerMode === 'full' ? '77px' : '125px'})`
        }}
        scrollableNodeProps={{ ref: scrollableNodeRef }}
      >
        <UserProfile drawerMode={drawerMode} />
        <NavBarDivider />

        <Box className={classes.swipeableViewsWrapper}>
          <SwipeableViews
            index={activeNav}
            animateHeight
            action={({ updateHeight }) => {
              ref.current = updateHeight;
            }}
          >
            <MainPageMenu index={0} drawerMode={drawerMode} />
            <MyCollectionPageMenu index={1} drawerMode={drawerMode} />
          </SwipeableViews>
        </Box>
      </SimpleBar>
    </Drawer>
  );
};

NavBar.propTypes = {
  bannerHeight: PropTypes.number
};

NavBar.defaultProps = {
  bannerHeight: 0
};

export default NavBar;
